/**
 * ServiceChat.jsx — Iter 66, generalised in Iter 69.
 *
 * A two-party thread. Same component for every side of every flow: the server
 * decides who the caller is and returns `side`, so nothing here needs to know
 * whether it's mounted in the founder's service wizard, the vendor queue, a
 * founder's credit application or the admin credit console.
 *
 * `basePath` picks the resource the thread hangs off:
 *   /service-requests      founder <-> fulfilment partner   (Iter 66)
 *   /credit/applications   founder <-> Founderboard Credit  (Iter 69)
 * Both expose GET/POST {basePath}/{id}/messages with the same payload shape.
 *
 * The thread doesn't always exist — a service request needs its agreement signed,
 * a credit application needs submitting. Callers check that themselves and render
 * a locked panel instead; this component assumes it's allowed to load.
 *
 * Polls while mounted rather than holding a socket: message volume on one of
 * these is a handful over days, and the backend is a plain REST monolith.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { ChatCircleDots, Lock, PaperPlaneTilt, Spinner } from '@phosphor-icons/react';

const POLL_MS = 20000;
const MAX_CHARS = 4000;

/** Message timestamps are relative for the last day, then absolute. */
const stamp = (iso) => {
  if (!iso) return '';
  const then = new Date(iso);
  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return then.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
};

const ServiceChat = ({
  requestId,
  accent,
  counterpartyFallback,
  onRead,
  compact = false,
  basePath = '/service-requests',
  placeholder,
  emptyHint,
}) => {
  const [messages, setMessages] = useState([]);
  const [side, setSide] = useState('');
  const [counterparty, setCounterparty] = useState(counterpartyFallback || '');
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  // onRead fires once per successful fetch to clear the parent's unread badge.
  // Held in a ref so a parent that re-creates the callback can't restart polling.
  const onReadRef = useRef(onRead);
  onReadRef.current = onRead;

  const load = useCallback(async ({ quiet } = {}) => {
    try {
      const r = await api.get(`${basePath}/${requestId}/messages`);
      setMessages(r.data.messages || []);
      setSide(r.data.side || '');
      if (r.data.counterparty) setCounterparty(r.data.counterparty);
      onReadRef.current?.();
    } catch (e) {
      if (!quiet) toast.error(e.response?.data?.detail || 'Could not load the conversation');
    } finally { setLoading(false); }
  }, [requestId, basePath]);

  useEffect(() => {
    load();
    const t = setInterval(() => load({ quiet: true }), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  // Pin to the newest message whenever the thread grows.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      const r = await api.post(`${basePath}/${requestId}/messages`, { body: text });
      // Append optimistically rather than refetching — the server hands back the
      // stored row, so this is the same object a reload would produce.
      setMessages((prev) => [...prev, r.data.message]);
      setBody('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send'); }
    finally { setSending(false); }
  };

  const onKeyDown = (e) => {
    // Enter sends, Shift+Enter breaks the line — the convention every founder
    // already has muscle memory for.
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div data-testid={`service-chat-${requestId}`}>
      <div className="flex items-center gap-2 mb-2.5 flex-wrap">
        <ChatCircleDots size={15} weight="fill" style={{ color: accent }} className="flex-shrink-0" />
        <div className="text-[15px] font-heading font-bold text-text-primary">
          Messages{counterparty ? <span className="text-text-muted font-normal"> · {counterparty}</span> : null}
        </div>
        <span className="text-[11px] text-text-muted ml-auto tabular-nums">
          {messages.length} message{messages.length === 1 ? '' : 's'}
        </span>
      </div>

      <div ref={scrollRef}
        className={`rounded-xl border border-border bg-background p-3 overflow-y-auto space-y-2 ${compact ? 'max-h-[22rem]' : 'max-h-[26rem]'}`}
        data-testid={`service-chat-thread-${requestId}`}>
        {loading ? (
          <div className="text-[12px] text-text-muted py-4 text-center">Loading conversation…</div>
        ) : messages.length === 0 ? (
          <div className="text-[12px] text-text-muted py-5 text-center leading-relaxed" data-testid={`service-chat-empty-${requestId}`}>
            {emptyHint || <>
              No messages yet. Anything you send here reaches {counterparty || 'the other party'} directly, and
              Founderboard can see the thread — so questions about scope, documents or timelines all belong here.
            </>}
          </div>
        ) : messages.map((m) => {
          const mine = m.author_side === side;
          const isPlatform = m.author_side === 'admin';
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
              data-testid={`service-chat-message-${m.id}`}>
              {/* One squared-off corner on the sender's side gives each bubble a
                  tail, so a run of consecutive messages still reads as directional.
                  Timestamps use a fixed colour rather than opacity — translucent
                  text over a saturated fill is what makes bubbles look muddy. */}
              <div
                className={`max-w-[85%] px-3 py-2 antialiased shadow-sm ${
                  mine ? 'rounded-2xl rounded-br-md text-white' : 'rounded-2xl rounded-bl-md bg-surface border'
                } ${!mine && !isPlatform ? 'border-border' : ''}`}
                style={mine
                  ? { backgroundColor: accent }
                  : isPlatform ? { borderColor: `${accent}66`, backgroundColor: `${accent}0d` } : {}}
              >
                {!mine && (
                  <div className={`text-[11px] font-semibold mb-0.5 leading-tight ${isPlatform ? '' : 'text-text-secondary'}`}
                    style={isPlatform ? { color: accent } : {}}>
                    {m.author_name}{isPlatform ? ' · Founderboard' : ''}
                  </div>
                )}
                <div className={`text-[13px] leading-[1.45] whitespace-pre-wrap break-words ${mine ? '' : 'text-text-primary'}`}>
                  {m.body}
                </div>
                <div className="text-[10px] mt-1 leading-none tabular-nums"
                  style={{ color: mine ? 'rgba(255,255,255,0.82)' : undefined }}>
                  <span className={mine ? '' : 'text-text-muted'}>{stamp(m.created_at)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-2.5 flex items-end gap-2">
        <textarea
          rows={compact ? 1 : 2}
          value={body}
          maxLength={MAX_CHARS}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={onKeyDown}
          data-testid={`service-chat-input-${requestId}`}
          placeholder={placeholder || `Message ${counterparty || 'your partner'}…`}
          className="flex-1 bg-background border border-border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-text-primary" />
        <button onClick={send} disabled={sending || !body.trim()}
          data-testid={`service-chat-send-${requestId}`}
          className="px-3 py-2.5 rounded-lg text-white inline-flex items-center gap-1.5 text-xs font-semibold disabled:opacity-50 flex-shrink-0"
          style={{ backgroundColor: accent }}>
          {sending ? <Spinner size={12} weight="bold" className="animate-spin" /> : <PaperPlaneTilt size={12} weight="bold" />}
          {sending ? 'Sending…' : 'Send'}
        </button>
      </div>
      <div className="text-[10px] text-text-muted mt-1.5">
        Enter to send, Shift+Enter for a new line. Both parties are emailed on every message.
      </div>
    </div>
  );
};

/** Shown in place of the thread before the agreement is signed. */
export const ServiceChatLocked = ({ accent, testid = 'service-chat-locked' }) => (
  <div className="rounded-xl border border-dashed border-border bg-background p-4 flex items-start gap-2.5"
    data-testid={testid}>
    <Lock size={14} weight="fill" className="text-text-muted flex-shrink-0 mt-0.5" />
    <div>
      <div className="text-sm font-semibold text-text-primary">Messaging opens once the agreement is signed</div>
      <p className="text-[11px] text-text-muted leading-relaxed mt-0.5">
        Accept the three-party agreement and you&rsquo;ll be able to message your partner directly from here —
        with Founderboard on the thread the whole way.
      </p>
    </div>
  </div>
);

export default ServiceChat;
